import React, { useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router";
import styled from "styled-components";
import Layout from "../components/Layout";
import Modal from "../components/Modal";
import BlackBanner from "../components/BlackBanner";
import { IconYoutube } from "../components/Icons";
import { C, F, CARD_RING, ring, Button, LinkButton, Container, Section, H2, ScanLines } from "../styles";
import { AREAS, getArea, getCategory } from "../data/calendar";
import { getStatus } from "../utils/status";
import { useNow } from "../utils/time";
import useStudent from "../utils/useStudent";
import { getGabarito } from "../service";

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 9px 16px;
  border-radius: 20px;
  border: none;
  font: ${(p) => (p.active ? 700 : 600)} 13px/1 ${F.title};
  background: ${(p) => (p.active ? C.yellow : "transparent")};
  color: ${(p) => (p.active ? C.blue : p.disabled ? "rgba(255,255,255,0.5)" : C.white)};
  box-shadow: ${(p) => (p.active ? "none" : ring(1, p.disabled ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.3)"))};
  cursor: ${(p) => (p.disabled || p.active ? "default" : "pointer")};
`;

const Result = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: ${C.navy};
  box-shadow: ${CARD_RING};
  padding: clamp(28px, 4vw, 44px) 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  > * {
    position: relative;
  }
`;

const CatTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;
  border-radius: 20px;
  background: ${C.yellow};
  color: ${C.blue};
  font: 700 13px/1.25 ${F.title};
  letter-spacing: 0.04em;
`;

const Score = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  b {
    font: 400 clamp(72px, 9vw, 108px) / 1 ${F.num};
    color: ${C.yellow};
  }
  span {
    font: 600 clamp(20px, 2.2vw, 26px) / 1.2 ${F.title};
  }
`;

const ResultMsg = styled.span`
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  max-width: 560px;
`;

const ResultTitle = styled.span`
  font: 700 clamp(22px, 2.6vw, 30px) / 1.15 ${F.title};
  letter-spacing: -0.03em;
`;

const TableHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Tally = styled.span`
  font: 600 13px/1 ${F.title};
  color: rgba(255, 255, 255, 0.75);
`;

const Table = styled.div`
  border-radius: 16px;
  overflow: hidden;
  background: ${C.navy};
  box-shadow: ${ring(1, "rgba(255,255,255,0.12)")};
`;

// No mobile a tabela vira lista: o enunciado some e as colunas encolhem.
const GridRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px 72px 36px 96px;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, ${(p) => (p.head ? 0.1 : 0.08)});
  &:last-child {
    border-bottom: none;
  }
  ${(p) =>
    p.head &&
    `
    font: 600 12px/1.2 ${F.title};
    letter-spacing: 0.04em;
    color: ${C.light};
  `}
  @media (max-width: 640px) {
    grid-template-columns: 32px 1fr 1fr 28px 72px;
    gap: 8px;
    padding: 12px;
    .statement {
      display: none;
    }
  }
`;

const Center = styled.span`
  text-align: center;
  font: 700 14px/1 ${F.title};
  color: ${(p) => p.color || C.white};
`;

const QCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  i {
    font-style: normal;
    flex: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    box-shadow: ${ring(1.5, C.light)};
    color: ${C.light};
    display: flex;
    align-items: center;
    justify-content: center;
    font: 700 11px/1 ${F.title};
  }
  span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Mark = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font: 800 12px/1 ${F.title};
  background: ${(p) => (p.hit === null ? "rgba(255,255,255,0.2)" : p.hit ? C.light : C.orange)};
  color: ${C.blue};
`;

const SeeBtn = styled.button`
  font: 600 13px/1 ${F.title};
  background: transparent;
  color: ${C.white};
  border: none;
  box-shadow: ${ring(1.5, "rgba(255,255,255,0.45)")};
  border-radius: 20px;
  padding: 9px 0;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Ctas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  padding-top: 8px;
`;

const ResCard = styled.div`
  width: 100%;
  max-width: 632px;
  max-height: 90vh;
  border-radius: 16px;
  overflow: hidden;
  background: ${C.white};
  color: ${C.text};
  display: flex;
  flex-direction: column;
  box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.6);
`;

const ResHead = styled.div`
  background: ${C.blue};
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  span:first-child {
    font: 700 22px/1.1 ${F.title};
    letter-spacing: -0.02em;
    color: ${C.white};
  }
  span:last-child {
    padding: 6px 12px;
    border-radius: 20px;
    background: ${C.yellow};
    color: ${C.blue};
    font: 700 12px/1 ${F.title};
    white-space: nowrap;
  }
`;

const ResBody = styled.div`
  padding: 20px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  .resolution {
    font-size: 16px;
    line-height: 1.6;
    p {
      margin: 0 0 10px;
    }
    img {
      max-width: 100%;
      height: auto;
    }
  }
  > span {
    font-size: 14px;
    color: #555;
  }
  strong {
    font-weight: 700;
  }
`;

const ResFoot = styled.div`
  border-top: 1px solid #eeeeee;
  padding: 12px;
  display: flex;
  justify-content: center;
  button {
    width: 132px;
    font: 600 14px/1 ${F.title};
    background: transparent;
    color: ${C.blue};
    border: none;
    box-shadow: ${ring(1, C.blue)};
    border-radius: 16px;
    padding: 9px 0;
    cursor: pointer;
  }
`;

function ResolutionModal({ row, onClose }) {
  return (
    <Modal onClose={onClose} label={`Resolução da questão ${row.n}`}>
      <ResCard>
        <ResHead>
          <span>Resolução · questão {row.n}</span>
          <span>Gabarito: {row.right}</span>
        </ResHead>
        <ResBody>
          <div className="resolution" dangerouslySetInnerHTML={{ __html: row.resolution || "Essa questão ainda não tem resolução em texto." }} />
          <span>
            Sua resposta: <strong>{row.mine || "em branco"}</strong>
          </span>
        </ResBody>
        <ResFoot>
          <button type="button" onClick={onClose}>
            FECHAR
          </button>
        </ResFoot>
      </ResCard>
    </Modal>
  );
}

const VISIBLE_ROWS = 10;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function GabaritoPage() {
  const history = useHistory();
  const location = useLocation();
  const now = useNow();
  const { user, progress, periods, loading } = useStudent();
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null);

  const statuses = useMemo(() => {
    const out = {};
    AREAS.forEach((a) => (out[a.slug] = getStatus(a, now, progress, !!user, periods)));
    return out;
  }, [now, progress, user, periods]);

  const viewable = (slug) => statuses[slug] === "done" || statuses[slug] === "expired";
  const requested = getArea(new URLSearchParams(location.search).get("area"));
  const area = requested || AREAS.find((a) => viewable(a.slug)) || AREAS[0];

  useEffect(() => {
    setRows([]);
    setShowAll(false);
    if (viewable(area.slug)) getGabarito(area.slug).then(setRows, () => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area.slug, statuses[area.slug]]);

  if (!user) return <Redirect to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} />;

  const status = statuses[area.slug];
  const p = progress[area.slug] || { answers: {} };
  const submitted = status === "done";

  const hits = submitted ? p.score : 0;
  const cat = getCategory(hits);

  const tabLabel = (a) => {
    const s = statuses[a.slug];
    if (s === "locked") return `${a.name} · abre ${a.openLabel}`;
    if (s === "live") return `${a.name} · no ar`;
    return a.name;
  };

  return (
    <Layout title="Gabarito" user={user}>
      <Container gap={24}>
        <Tabs role="tablist">
          {AREAS.map((a) => (
            <Tab
              key={a.slug}
              role="tab"
              type="button"
              active={a.slug === area.slug}
              aria-selected={a.slug === area.slug}
              disabled={!viewable(a.slug) && a.slug !== area.slug}
              onClick={() => history.replace(`/gabarito?area=${a.slug}`)}
            >
              {tabLabel(a)}
            </Tab>
          ))}
        </Tabs>

        {loading ? null : !viewable(area.slug) ? (
          <Result>
            <ScanLines />
            <ResultTitle>{status === "live" ? "Esse simulado tá no ar" : "Esse simulado ainda não abriu"}</ResultTitle>
            <ResultMsg>
              {status === "live"
                ? "O gabarito aparece assim que você enviar as suas respostas."
                : `Ele entra no ar em ${area.openLabel} às 00h. O gabarito aparece assim que você enviar.`}
            </ResultMsg>
            {status === "live" && (
              <Button variant="yellow" onClick={() => history.push(`/simulados?comecar=${area.slug}`)} style={{ marginTop: 8 }}>
                Fazer o simulado →
              </Button>
            )}
          </Result>
        ) : (
          <Section gap={24}>
            <Result>
              <ScanLines />
              {submitted ? (
                <>
                  <CatTag>{cat.name}</CatTag>
                  <Score>
                    <b>{hits}</b>
                    <span>de {rows.length} questões</span>
                  </Score>
                  <ResultMsg>{cat.msg}</ResultMsg>
                </>
              ) : (
                <>
                  <CatTag>GABARITO COMENTADO</CatTag>
                  <ResultTitle>{area.name}</ResultTitle>
                  <ResultMsg>Você não enviou esse simulado até {area.closeLabel}, então ele fica sem pontuação. Mas dá pra revisar cada questão com a resolução.</ResultMsg>
                </>
              )}
            </Result>

            <Section gap={12}>
              <TableHead>
                <H2 as="span">Gabarito</H2>
                {submitted && (
                  <Tally>
                    {plural(hits, "acerto", "acertos")} · {plural(rows.length - hits, "erro", "erros")}
                  </Tally>
                )}
              </TableHead>
              <Table>
                <GridRow head>
                  <span>Questão</span>
                  <span style={{ textAlign: "center" }}>Sua resposta</span>
                  <span style={{ textAlign: "center" }}>Gabarito</span>
                  <span />
                  <span style={{ textAlign: "center" }}>Resolução</span>
                </GridRow>
                {(showAll ? rows : rows.slice(0, VISIBLE_ROWS)).map((r) => (
                  <GridRow key={r.n}>
                    <QCell>
                      <i>{r.n}</i>
                      <span className="statement">{r.statement}</span>
                    </QCell>
                    <Center>{r.mine || "—"}</Center>
                    <Center color={C.yellow}>{r.right}</Center>
                    <Mark hit={r.hit} aria-label={r.hit === null ? "" : r.hit ? "Acertou" : "Errou"}>
                      {r.hit === null ? "" : r.hit ? "✓" : "✕"}
                    </Mark>
                    <SeeBtn type="button" onClick={() => setOpen(r)}>
                      VER
                    </SeeBtn>
                  </GridRow>
                ))}
              </Table>
              {!showAll && rows.length > VISIBLE_ROWS && (
                <Button variant="outlineYellow" pad="11px 20px" style={{ alignSelf: "flex-start" }} onClick={() => setShowAll(true)}>
                  Ver todas as {rows.length}
                </Button>
              )}
            </Section>

            <BlackBanner now={now} />
          </Section>
        )}

        <Ctas>
          <Button variant="yellow" size={15} pad="16px 22px" onClick={() => history.push("/simulados")}>
            Fazer o próximo simulado →
          </Button>
          <LinkButton
            variant="outline"
            size={15}
            pad="16px 22px"
            href="/ao-vivo"
            onClick={(e) => {
              e.preventDefault();
              history.push("/ao-vivo");
            }}
            style={{ fontWeight: 700 }}
          >
            <IconYoutube />
            Próximos aulões ao vivo
          </LinkButton>
        </Ctas>
      </Container>
      {open && <ResolutionModal row={open} onClose={() => setOpen(null)} />}
    </Layout>
  );
}
